import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

// Display formats such as 'dddd Do MMMM' need the ordinal (Do) token.
dayjs.extend(advancedFormat);

const ARIA_DATE_FORMAT = 'D MMMM YYYY';
// flatpickr's altFormat is replaced by this token so the alt input is always rendered by the dateAdapter.
const DISPLAY_FORMAT_TOKEN = '__lta_display__';

const A11Y = {
	chooseDate: 'Choose date',
	chooseDateRange: 'Choose date range',
	changeDate: 'Change date',
	changeDateRange: 'Change date range',
	calendarLabel: 'Choose a date',
	calendarLabelRange: 'Choose a date range',
	previousMonth: 'Previous month',
	nextMonth: 'Next month',
	month: 'Month',
	year: 'Year',
	selectedDatePrefix: 'Selected date is',
	selectedRangePrefix: 'Selected date range is',
	calendarOpened: 'Calendar opened'
};

const RANGE_SEPARATOR = ' to ';
const isRangeMode = (mode) => String(mode || '').toLowerCase() === 'range';
const toIso = (date) => dayjs(date).format('YYYY-MM-DD');
const toAriaDate = (date) => dayjs(date).format(ARIA_DATE_FORMAT);

const parseDateValue = (value, mode) => {
	if (!value) {
		return null;
	}
	if (!isRangeMode(mode)) {
		return value;
	}
	const parts = String(value)
		.split(/\s+to\s+|,\s*/)
		.map((part) => part.trim())
		.filter(Boolean);
	if (parts.length === 0) {
		return null;
	}
	return parts.length === 1 ? [parts[0]] : [parts[0], parts[1]];
};

const formatRangeIso = (dates = []) => {
	if (!dates.length) {
		return '';
	}
	if (dates.length === 1) {
		return toIso(dates[0]);
	}
	return `${toIso(dates[0])}${RANGE_SEPARATOR}${toIso(dates[1])}`;
};

const formatRangeDisplay = (dates = [], formatFn) => {
	if (!dates.length || typeof formatFn !== 'function') {
		return '';
	}
	if (dates.length === 1) {
		return formatFn(dates[0]);
	}
	return `${formatFn(dates[0])}${RANGE_SEPARATOR}${formatFn(dates[1])}`;
};

let datePickerUid = 0;
const nextDatePickerId = (prefix) => `${prefix}-${++datePickerUid}`;

const MONTH_NAMES = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December'
];

const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const locale = {
	firstDayOfWeek: 1,
	weekdays: {
		shorthand: WEEKDAYS_SHORT,
		longhand: WEEKDAYS_LONG
	},
	months: {
		shorthand: MONTH_NAMES_SHORT,
		longhand: MONTH_NAMES
	}
};

const monthYearLabel = (instance) => `${MONTH_NAMES[instance.currentMonth]} ${instance.currentYear}`;

const appendToken = (existing, token) => {
	const tokens = (existing || '').split(/\s+/).filter(Boolean);
	if (!tokens.includes(token)) {
		tokens.push(token);
	}
	return tokens.join(' ');
};

const ensureHeading = (calendar) => {
	let heading = calendar.querySelector('.lta-datepicker-heading');
	if (!heading) {
		heading = document.createElement('h2');
		heading.className = 'lta-datepicker-heading lta-datepicker-vh';
		calendar.prepend(heading);
	}
	if (!heading.id) {
		heading.id = `${calendar.id}-heading`;
	}
	return heading;
};

const ensureLiveRegion = (calendar) => {
	let live = calendar.querySelector('.lta-datepicker-live');
	if (!live) {
		live = document.createElement('div');
		live.className = 'lta-datepicker-live lta-datepicker-vh';
		live.setAttribute('aria-live', 'polite');
		live.setAttribute('aria-atomic', 'true');
		calendar.prepend(live);
	}
	return live;
};

const updateDialogHeading = (instance) => {
	const heading = ensureHeading(instance.calendarContainer);
	const label = isRangeMode(instance._ltaMode) ? A11Y.calendarLabelRange : A11Y.calendarLabel;
	heading.textContent = `${label}, ${monthYearLabel(instance)}`;
	return heading;
};

const describeSelectedDates = (dates = [], mode) => {
	if (!dates.length) {
		return '';
	}
	if (isRangeMode(mode) && dates.length > 1) {
		return `${A11Y.selectedRangePrefix} ${toAriaDate(dates[0])} to ${toAriaDate(dates[1])}.`;
	}
	return `${A11Y.selectedDatePrefix} ${toAriaDate(dates[0])}.`;
};

const announceCalendarState = (instance, prefix) => {
	const live = ensureLiveRegion(instance.calendarContainer);
	const selected = describeSelectedDates(instance.selectedDates, instance._ltaMode);
	live.textContent = [prefix, `${monthYearLabel(instance)}.`, selected].filter(Boolean).join(' ');
};

const announceSelectedDate = (instance, dates) => {
	const live = ensureLiveRegion(instance.calendarContainer);
	const selectedDates = Array.isArray(dates) ? dates : dates ? [dates] : [];
	live.textContent = describeSelectedDates(selectedDates, instance._ltaMode).replace(/\.$/, '');
};

const updateTriggerSelectedDate = (instance, dates) => {
	const trigger = instance._ltaTrigger;
	if (!trigger) {
		return;
	}

	const selectedDates = Array.isArray(dates) ? dates : dates ? [dates] : [];
	const isRange = isRangeMode(instance._ltaMode);
	const chooseLabel = isRange ? A11Y.chooseDateRange : A11Y.chooseDate;
	const changeLabel = isRange ? A11Y.changeDateRange : A11Y.changeDate;
	const selectedSummary = selectedDates.length
		? selectedDates.map((date) => toAriaDate(date)).join(' to ')
		: '';

	if (trigger.tagName === 'BUTTON') {
		if (!trigger.getAttribute('aria-labelledby')) {
			trigger.setAttribute(
				'aria-label',
				selectedSummary ? `${changeLabel}, ${selectedSummary}` : chooseLabel
			);
		}
		return;
	}

	const descriptionId = `${instance.calendarContainer.id}-selected`;
	let description = document.getElementById(descriptionId);
	if (!description) {
		description = document.createElement('span');
		description.id = descriptionId;
		description.className = 'lta-datepicker-vh';
		trigger.after(description);
		trigger.setAttribute('aria-describedby', appendToken(trigger.getAttribute('aria-describedby'), descriptionId));
	}

	description.textContent = describeSelectedDates(selectedDates, instance._ltaMode).replace(/\.$/, '');
};

const syncValidity = (instance, input) => {
	const control = instance.altInput;
	if (!control) {
		return;
	}

	const wrapper =
		input.closest('.lta-datepicker, .lta-datepicker__wrapper, .lta-text-input__wrapper') || input.parentElement;
	const isInvalid =
		input.getAttribute('aria-invalid') === 'true' ||
		input.classList.contains('is-invalid') ||
		Boolean(wrapper?.classList.contains('is-invalid')) ||
		Boolean(wrapper?.closest('.is-invalid'));

	control.setAttribute('aria-invalid', isInvalid ? 'true' : 'false');

	const errorNode = wrapper?.querySelector('.lta-text-input--error, .lta-datepicker__label-instruction');
	const describedBy = new Set(
		`${input.getAttribute('aria-describedby') || ''} ${control.getAttribute('aria-describedby') || ''}`
			.split(/\s+/)
			.filter(Boolean)
	);
	if (errorNode && errorNode.textContent.trim()) {
		if (!errorNode.id) {
			errorNode.id = nextDatePickerId('lta-datepicker-error');
		}
		describedBy.add(errorNode.id);
	}
	if (describedBy.size > 0) {
		control.setAttribute('aria-describedby', [...describedBy].join(' '));
	}

	if (input.disabled) {
		control.setAttribute('disabled', 'true');
	} else {
		control.removeAttribute('disabled');
	}

	if (input.hasAttribute('required') || input.getAttribute('aria-required') === 'true') {
		control.setAttribute('aria-required', 'true');
	} else {
		control.removeAttribute('aria-required');
	}
};

const observeValidity = (instance, input) => {
	if (input.dataset.ltaValidityObserved === 'true') {
		return;
	}
	input.dataset.ltaValidityObserved = 'true';
	const wrapper =
		input.closest('.lta-datepicker, .lta-datepicker__wrapper, .lta-text-input__wrapper') || input.parentElement;
	const observer = new MutationObserver(() => {
		const min = input.getAttribute('min');
		const max = input.getAttribute('max');
		instance.set?.('minDate', min || null);
		instance.set?.('maxDate', max || null);
		syncValidity(instance, input);
	});
	observer.observe(input, {
		attributes: true,
		attributeFilter: ['aria-invalid', 'class', 'disabled', 'required', 'aria-required', 'aria-describedby', 'min', 'max']
	});
	if (wrapper) {
		observer.observe(wrapper, { attributes: true, attributeFilter: ['class'] });
		const errorContainer = wrapper.querySelector('.lta-text-input--error, .lta-datepicker__label-instruction');
		if (errorContainer) {
			observer.observe(errorContainer, { childList: true, characterData: true, subtree: true });
		}
	}
	syncValidity(instance, input);
};

const enhanceDayCell = (dayElem) => {
	const isDisabled = dayElem.classList.contains('flatpickr-disabled');
	const isSelected =
		dayElem.classList.contains('selected') ||
		dayElem.classList.contains('startRange') ||
		dayElem.classList.contains('endRange');
	const isToday = dayElem.classList.contains('today');

	dayElem.setAttribute('role', 'gridcell');
	dayElem.tabIndex = -1;
	if (dayElem.dateObj) {
		dayElem.setAttribute('aria-label', toAriaDate(dayElem.dateObj));
	}
	if (isDisabled) {
		dayElem.setAttribute('aria-disabled', 'true');
	} else {
		dayElem.removeAttribute('aria-disabled');
	}
	dayElem.setAttribute('aria-selected', isSelected ? 'true' : 'false');
	if (isToday) {
		dayElem.setAttribute('aria-current', 'date');
	} else {
		dayElem.removeAttribute('aria-current');
	}
};

const syncGridTabOrder = (instance) => {
	const days = instance.calendarContainer.querySelectorAll('.flatpickr-day');
	let preferred = null;
	days.forEach((day) => {
		day.tabIndex = -1;
		if (day.classList.contains('selected') && !day.classList.contains('flatpickr-disabled')) {
			preferred = day;
		}
	});
	if (!preferred) {
		preferred =
			instance.calendarContainer.querySelector('.flatpickr-day.today:not(.flatpickr-disabled)') ||
			instance.calendarContainer.querySelector('.flatpickr-day:not(.flatpickr-disabled)');
	}
	if (preferred) {
		preferred.tabIndex = 0;
	}
	return preferred;
};

const focusSelectedDay = (instance) => {
	const preferred = syncGridTabOrder(instance);
	preferred?.focus({ preventScroll: true });
};

const enhanceWeekdays = (calendar) => {
	const weekdayRow =
		calendar.querySelector('.flatpickr-weekdaycontainer') || calendar.querySelector('.flatpickr-weekdays');
	weekdayRow?.setAttribute('role', 'row');
	const weekdays = calendar.querySelectorAll('.flatpickr-weekday');
	weekdays.forEach((weekday, index) => {
		const longName = locale.weekdays.longhand[(index + locale.firstDayOfWeek) % 7];
		weekday.setAttribute('role', 'columnheader');
		weekday.setAttribute('abbr', longName);
		weekday.setAttribute('aria-label', longName);
	});
};

const enhanceGrid = (calendar) => {
	const days = calendar.querySelector('.flatpickr-days');
	days?.setAttribute('role', 'grid');
	days?.setAttribute('aria-labelledby', `${calendar.id}-heading`);
	calendar.querySelector('.dayContainer')?.setAttribute('role', 'rowgroup');
};

const enhanceMonthControls = (calendar) => {
	const monthSelect = calendar.querySelector('.flatpickr-monthDropdown-months');
	monthSelect?.setAttribute('aria-label', A11Y.month);
	monthSelect?.setAttribute('autocomplete', 'off');

	const prev = calendar.querySelector('.flatpickr-prev-month');
	const next = calendar.querySelector('.flatpickr-next-month');
	prev?.setAttribute('aria-label', A11Y.previousMonth);
	prev?.setAttribute('role', 'button');
	prev?.setAttribute('tabindex', '0');
	next?.setAttribute('aria-label', A11Y.nextMonth);
	next?.setAttribute('role', 'button');
	next?.setAttribute('tabindex', '0');
};

const syncYearDropdown = (instance) => {
	const calendar = instance.calendarContainer;
	const wrapper = calendar.querySelector('.numInputWrapper');
	let yearSelect = calendar.querySelector('.lta-flatpickr-year');
	const minYear = instance.config?.minDate instanceof Date ? instance.config.minDate.getFullYear() : instance.currentYear - 12;
	const maxYear = instance.config?.maxDate instanceof Date ? instance.config.maxDate.getFullYear() : instance.currentYear + 12;

	if (!yearSelect) {
		yearSelect = document.createElement('select');
		yearSelect.className = 'lta-flatpickr-year';
		yearSelect.setAttribute('aria-label', A11Y.year);
		yearSelect.setAttribute('autocomplete', 'off');
		yearSelect.addEventListener('change', () => {
			instance.changeYear?.(Number(yearSelect.value));
		});
		if (wrapper) {
			wrapper.replaceWith(yearSelect);
		} else {
			calendar.querySelector('.flatpickr-current-month')?.append(yearSelect);
		}
	}

	yearSelect.innerHTML = '';
	for (let year = minYear; year <= maxYear; year += 1) {
		const option = document.createElement('option');
		option.value = String(year);
		option.textContent = String(year);
		if (year === instance.currentYear) {
			option.selected = true;
		}
		yearSelect.append(option);
	}
};

const getFocusableInDialog = (calendar) => {
	const nodes = calendar.querySelectorAll(
		'select, .lta-flatpickr-year, .flatpickr-prev-month, .flatpickr-next-month, .flatpickr-day[tabindex="0"]'
	);
	return [...nodes].filter((el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true');
};

const bindFocusTrap = (instance) => {
	const calendar = instance.calendarContainer;
	if (calendar.dataset.ltaFocusTrap === 'true') {
		return;
	}
	calendar.dataset.ltaFocusTrap = 'true';

	calendar.addEventListener('keydown', (event) => {
		if (!instance.isOpen) {
			return;
		}

		if (event.key === 'Escape') {
			event.preventDefault();
			event.stopPropagation();
			instance._ltaRestoreFocus = true;
			instance.close();
			return;
		}

		if (event.key !== 'Tab') {
			return;
		}

		const focusable = getFocusableInDialog(calendar);
		if (focusable.length === 0) {
			event.preventDefault();
			return;
		}

		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		const active = document.activeElement;

		if (event.shiftKey && (active === first || !calendar.contains(active))) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && (active === last || !calendar.contains(active))) {
			event.preventDefault();
			first.focus();
		}
	});
};

const bindGridKeyboard = (instance) => {
	const calendar = instance.calendarContainer;
	if (calendar.dataset.ltaGridKeys === 'true') {
		return;
	}
	calendar.dataset.ltaGridKeys = 'true';

	calendar.addEventListener('keydown', (event) => {
		const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown'];
		if (!keys.includes(event.key) || !instance.isOpen) {
			return;
		}

		const days = [
			...calendar.querySelectorAll('.flatpickr-day:not(.flatpickr-disabled):not(.prevMonthDay):not(.nextMonthDay)')
		];
		const current = document.activeElement;
		const index = days.indexOf(current);
		if (index < 0) {
			return;
		}

		event.preventDefault();
		let next = index;
		if (event.key === 'ArrowLeft') {
			next = Math.max(0, index - 1);
		}
		if (event.key === 'ArrowRight') {
			next = Math.min(days.length - 1, index + 1);
		}
		if (event.key === 'ArrowUp') {
			next = Math.max(0, index - 7);
		}
		if (event.key === 'ArrowDown') {
			next = Math.min(days.length - 1, index + 7);
		}
		if (event.key === 'Home') {
			next = 0;
		}
		if (event.key === 'End') {
			next = days.length - 1;
		}
		if (event.key === 'PageUp') {
			instance.changeMonth?.(-1);
			return;
		}
		if (event.key === 'PageDown') {
			instance.changeMonth?.(1);
			return;
		}

		days[next]?.focus({ preventScroll: true });
		syncGridTabOrder(instance);
	});
};

const bindTrigger = (instance, input, options) => {
	const clickOpens = options.clickOpens !== false;
	const altInput = instance.altInput;
	const trigger = options.trigger || altInput;
	instance._ltaTrigger = trigger;
	if (!trigger) {
		return;
	}

	trigger.setAttribute('aria-haspopup', 'dialog');
	trigger.setAttribute('aria-expanded', instance.isOpen ? 'true' : 'false');
	trigger.setAttribute('aria-controls', instance.calendarContainer.id);

	if (trigger.tagName === 'BUTTON' && !trigger.getAttribute('aria-label') && !trigger.getAttribute('aria-labelledby')) {
		trigger.setAttribute(
			'aria-label',
			isRangeMode(instance._ltaMode) ? A11Y.chooseDateRange : A11Y.chooseDate
		);
	}

	if (trigger !== altInput && trigger.dataset.ltaA11yOpenBound !== 'true') {
		trigger.dataset.ltaA11yOpenBound = 'true';
		trigger.addEventListener('click', (event) => {
			event.preventDefault();
			instance.open();
		});
		trigger.addEventListener('keydown', (event) => {
			if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
				event.preventDefault();
				instance.open();
			}
		});
	}

	if (clickOpens && altInput && trigger === altInput) {
		const selectorId =
			altInput.id && typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(altInput.id) : altInput.id;
		const associatedLabel = selectorId ? document.querySelector(`label[for="${selectorId}"]`) : null;
		if (!associatedLabel && !altInput.getAttribute('aria-label') && !altInput.getAttribute('aria-labelledby')) {
			altInput.setAttribute(
				'aria-label',
				isRangeMode(instance._ltaMode) ? A11Y.chooseDateRange : A11Y.chooseDate
			);
		}

		if (altInput.dataset.ltaA11yOpenBound !== 'true') {
			altInput.dataset.ltaA11yOpenBound = 'true';
			altInput.addEventListener('keydown', (event) => {
				if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
					event.preventDefault();
					instance.open();
				}
			});
		}
	}

	if (instance.selectedDates?.length) {
		updateTriggerSelectedDate(instance, instance.selectedDates);
	}
};

const enhanceAccessibility = (instance, input, options) => {
	const calendar = instance.calendarContainer;
	calendar.id = calendar.id || nextDatePickerId('lta-datepicker-dialog');
	calendar.classList.add('lta-datepicker-calendar');
	calendar.setAttribute('role', 'dialog');
	calendar.setAttribute('aria-modal', 'true');
	calendar.setAttribute('aria-hidden', instance.isOpen ? 'false' : 'true');
	updateDialogHeading(instance);
	calendar.setAttribute('aria-labelledby', ensureHeading(calendar).id);
	ensureLiveRegion(calendar);
	enhanceMonthControls(calendar);
	enhanceWeekdays(calendar);
	enhanceGrid(calendar);
	syncYearDropdown(instance);
	bindFocusTrap(instance);
	bindGridKeyboard(instance);
	bindTrigger(instance, input, options);
};

const addCalendarChrome = (instance, input, options) => {
	enhanceAccessibility(instance, input, options);
};

const isInsidePicker = (instance, input, target) => {
	if (!(target instanceof Node)) {
		return false;
	}
	if (instance.calendarContainer?.contains(target)) {
		return true;
	}
	if (instance.altInput && (target === instance.altInput || instance.altInput.contains(target))) {
		return true;
	}
	if (target === input || input.contains(target)) {
		return true;
	}
	if (instance._ltaTrigger && (target === instance._ltaTrigger || instance._ltaTrigger.contains(target))) {
		return true;
	}
	return Boolean(target.closest?.('.lta-datepicker__wrapper, .lta-datepicker'));
};

const overlapCalendarOnInput = (instance) => {
	const trigger = instance.altInput || instance._ltaTrigger || instance.input;
	const calendar = instance.calendarContainer;
	if (!trigger || !calendar) {
		return;
	}

	const rect = trigger.getBoundingClientRect();
	calendar.style.position = 'absolute';
	calendar.style.top = `${rect.top + window.scrollY}px`;
	calendar.style.left = `${rect.left + window.scrollX}px`;
	calendar.style.right = 'auto';
	calendar.style.marginTop = '0';
	calendar.classList.remove('arrowTop', 'arrowBottom', 'arrowLeft', 'arrowRight');
};

const dispatchDuetEvent = (input, name, detail = {}) => {
	input.dispatchEvent(
		new CustomEvent(name, {
			bubbles: true,
			detail: {
				component: 'duet-date-picker',
				...detail
			}
		})
	);
};

export function createLtaDatePicker(input, options = {}) {
	if (!input) {
		return null;
	}

	if (input._ltaDatePicker) {
		return input._ltaDatePicker;
	}

	if (typeof window.flatpickr !== 'function') {
		return null;
	}

	const displayFormat = options.displayFormat || 'YYYY-MM-DD';
	const clickOpens = options.clickOpens !== false;
	const mode = isRangeMode(options.mode) ? 'range' : 'single';
	const isRange = mode === 'range';
	let isReady = false;
	let suppressOpen = false;
	let disabledChecker = input.isDateDisabled || null;
	let dateAdapter = {
		format: (date) => dayjs(date).format(displayFormat)
	};

	let syncingValue = false;
	const nativeValue = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
	const initialValue = parseDateValue(input.value || input.getAttribute('value') || '', mode);

	const buildValueDetail = (selectedDates = []) => {
		if (isRange) {
			const value = formatRangeIso(selectedDates);
			const valueAsDate = selectedDates.length ? [...selectedDates] : null;
			return { value, valueAsDate };
		}
		const valueAsDate = selectedDates[0] || null;
		return {
			value: valueAsDate ? toIso(valueAsDate) : '',
			valueAsDate
		};
	};

	const applyDisplayValue = (instance) => {
		if (!instance?.altInput) {
			return;
		}
		instance.altInput.value = isRange
			? formatRangeDisplay(instance.selectedDates, dateAdapter?.format)
			: instance.selectedDates?.[0] && dateAdapter?.format
				? dateAdapter.format(instance.selectedDates[0])
				: '';
	};

	const syncNativeValue = (value) => {
		if (!nativeValue?.set) {
			input.setAttribute('value', value);
			return;
		}
		syncingValue = true;
		try {
			nativeValue.set.call(input, value);
		} finally {
			syncingValue = false;
		}
	};

	const commitPendingDate = (instance) => {
		applyDisplayValue(instance);
		const detail = buildValueDetail(instance.selectedDates);
		syncNativeValue(detail.value);
		return instance.selectedDates;
	};

	const fp = window.flatpickr(input, {
		mode,
		altInput: true,
		altFormat: DISPLAY_FORMAT_TOKEN,
		// flatpickr rewrites the alt input on set()/setDate()/redraw(); route it through the dateAdapter
		// so custom display formats (e.g. 'dddd Do MMMM') are not replaced.
		formatDate: (date, format, l10n) =>
			format === DISPLAY_FORMAT_TOKEN ? dateAdapter.format(date) : window.flatpickr.formatDate(date, format, l10n),
		altInputClass: options.altInputClass || 'lta-datepicker__input',
		dateFormat: 'Y-m-d',
		defaultDate: initialValue,
		minDate: input.getAttribute('min') || null,
		maxDate: input.getAttribute('max') || null,
		disableMobile: true,
		allowInput: false,
		clickOpens,
		closeOnSelect: true,
		animate: false,
		appendTo: document.body,
		position: overlapCalendarOnInput,
		locale: {
			...locale,
			rangeSeparator: RANGE_SEPARATOR
		},
		disable: disabledChecker ? [disabledChecker] : [],
		onDayCreate(_dObj, _dStr, _fp, dayElem) {
			enhanceDayCell(dayElem);
		},
		onReady(_selectedDates, _dateStr, instance) {
			instance._ltaMode = mode;
			instance.input.type = 'text';
			instance.input.classList.remove('lta-datepicker__input');
			instance.input.classList.add('lta-datepicker__native');
			instance.input.setAttribute('tabindex', '-1');
			instance.input.setAttribute('aria-hidden', 'true');
			if (instance.altInput) {
				if (input.id) {
					instance.altInput.id = input.id;
					input.removeAttribute('id');
				}
				instance.altInput.setAttribute('readonly', 'true');
				instance.altInput.setAttribute('autocomplete', 'off');
			}
			if (options.placeholder || input.getAttribute('placeholder')) {
				instance.altInput?.setAttribute('placeholder', options.placeholder || input.getAttribute('placeholder'));
			}
			addCalendarChrome(instance, input, options);
			observeValidity(instance, input);
			isReady = true;
			applyDisplayValue(instance);
		},
		onOpen(_selectedDates, _dateStr, instance) {
			if (suppressOpen) {
				instance.close();
				return;
			}
			document.body.classList.add('has-lta-flatpickr-open');
			instance.calendarContainer.setAttribute('aria-hidden', 'false');
			syncYearDropdown(instance);
			updateDialogHeading(instance);
			syncGridTabOrder(instance);
			instance._ltaTrigger?.setAttribute('aria-expanded', 'true');
			announceCalendarState(instance, A11Y.calendarOpened);
			dispatchDuetEvent(input, 'duetOpen', buildValueDetail(instance.selectedDates));
			overlapCalendarOnInput(instance);
			requestAnimationFrame(() => {
				if (!suppressOpen) {
					overlapCalendarOnInput(instance);
					focusSelectedDay(instance);
				}
			});
		},
		onMonthChange(_selectedDates, _dateStr, instance) {
			syncYearDropdown(instance);
			updateDialogHeading(instance);
			syncGridTabOrder(instance);
			announceCalendarState(instance);
		},
		onYearChange(_selectedDates, _dateStr, instance) {
			syncYearDropdown(instance);
			updateDialogHeading(instance);
			syncGridTabOrder(instance);
			announceCalendarState(instance);
		},
		onChange(selectedDates, _dateStr, instance) {
			if (syncingValue) {
				return;
			}
			const detail = buildValueDetail(selectedDates);
			syncNativeValue(detail.value);
			applyDisplayValue(instance);
			announceSelectedDate(instance, selectedDates);
			updateTriggerSelectedDate(instance, selectedDates);
			dispatchDuetEvent(input, 'duetChange', detail);
			if (typeof options.onChange === 'function') {
				options.onChange(detail.value, detail.valueAsDate);
			}
		},
		onClose(_selectedDates, _dateStr, instance) {
			commitPendingDate(instance);
			document.body.classList.remove('has-lta-flatpickr-open');
			instance.calendarContainer.setAttribute('aria-hidden', 'true');
			instance._ltaTrigger?.setAttribute('aria-expanded', 'false');
			dispatchDuetEvent(input, 'duetClose', buildValueDetail(instance.selectedDates));
			const restoreFocus = Boolean(instance._ltaRestoreFocus);
			instance._ltaRestoreFocus = false;
			suppressOpen = true;
			requestAnimationFrame(() => {
				if (restoreFocus) {
					instance._ltaTrigger?.focus({ preventScroll: true });
				}
				requestAnimationFrame(() => {
					suppressOpen = false;
				});
			});
			if (typeof options.onClose === 'function') {
				options.onClose();
			}
		}
	});

	fp._ltaMode = mode;

	if (nativeValue?.get && nativeValue?.set) {
		Object.defineProperty(input, 'value', {
			configurable: true,
			get() {
				return nativeValue.get.call(input);
			},
			set(nextValue) {
				const iso = nextValue || '';
				const currentValue = isRange
					? formatRangeIso(fp?.selectedDates || [])
					: fp?.selectedDates?.[0]
						? toIso(fp.selectedDates[0])
						: '';
				nativeValue.set.call(input, iso);
				if (!isReady || !fp || syncingValue || currentValue === iso) {
					return;
				}
				syncingValue = true;
				try {
					const parsed = parseDateValue(iso, mode);
					if (parsed) {
						fp.setDate(parsed, false);
					} else {
						fp.clear?.(false);
					}
					applyDisplayValue(fp);
				} finally {
					syncingValue = false;
				}
			}
		});
	}

	Object.defineProperty(input, 'dateAdapter', {
		configurable: true,
		get() {
			return dateAdapter;
		},
		set(adapter) {
			dateAdapter = adapter || dateAdapter;
			applyDisplayValue(fp);
		}
	});

	Object.defineProperty(input, 'isDateDisabled', {
		configurable: true,
		get() {
			return disabledChecker;
		},
		set(fn) {
			disabledChecker = fn;
			fp?.set('disable', fn ? [fn] : []);
		}
	});

	input.show = () => {
		if (input.disabled || suppressOpen) {
			return;
		}
		fp.open();
	};

	const openCalendar = fp.open.bind(fp);
	fp.open = (...args) => {
		if (suppressOpen) {
			return;
		}
		return openCalendar(...args);
	};

	const onDocumentPointerDown = (event) => {
		if (!fp.isOpen) {
			return;
		}
		if (isInsidePicker(fp, input, event.target)) {
			return;
		}
		commitPendingDate(fp);
		fp.close();
	};
	document.addEventListener('pointerdown', onDocumentPointerDown, true);
	document.addEventListener('mousedown', onDocumentPointerDown, true);

	if (typeof fp._positionCalendar === 'function') {
		fp._positionCalendar = () => overlapCalendarOnInput(fp);
	}

	const originalDestroy = fp.destroy?.bind(fp);
	fp.destroy = () => {
		document.removeEventListener('pointerdown', onDocumentPointerDown, true);
		document.removeEventListener('mousedown', onDocumentPointerDown, true);
		originalDestroy?.();
	};

	input._ltaDatePicker = fp;
	return fp;
}

export function initStandaloneDateInputs(root = document) {
	const inputs = root.querySelectorAll('.js-lta-date-input');
	for (const input of inputs) {
		if (input.closest('lta-date-picker')) {
			continue;
		}
		createLtaDatePicker(input);
	}
}
